"use client"

import { useAuthStore } from "@/stores/auth-store"

/**
 * The three per-widget dashboard trims, read once and shared by every role
 * variant that renders the widgets they gate.
 *
 * Why a hook here rather than self-gating inside the three widget components
 * (the {@link import('./my-tasks-widget').MyTasksWidget} pattern): every one of
 * these sits inside a grid whose column count is computed from how many
 * children survived gating (`CHART_GRID_COLS[charts.length]`). A widget that
 * mounts and returns `null` would still be counted, leaving a hole in the row —
 * the exact failure the admin variant's doc block warns about for
 * `TodayScheduleTimeline`. The caller must therefore know the widget is gone
 * BEFORE it lays the row out, which means reading the flag one level up.
 *
 * Each flag is default-TRUE on the backend, so this is opt-out: only a clinic
 * whose profile turns one off loses the widget.
 *
 * That polarity is also why these read `!== false` instead of going through
 * {@link import('@/lib/feature-flags').hasFlag}. hasFlag DENIES on a missing
 * key, which is right for a capability flag — an older backend that never heard
 * of the feature cannot serve it, so hiding is honest. Here the opposite holds:
 * the widget IS the old behaviour, so a backend that serves no such key is one
 * where every clinic should still see it. Denying would silently strip widgets
 * from every clinic on a version skew. Mirroring the backend default is the
 * honest answer instead.
 *
 * No hydration flash either way: the dispatcher in `app/(dashboard)/page.tsx`
 * renders a skeleton until `/auth/me` resolves, so no variant mounts before
 * `user.feature_flags` exists.
 */
export interface DashboardWidgetFlags {
  /** "Leads by status" donut ("Patients by status" under patients terminology). */
  showLeadsStatusChart: boolean
  /** Quote pipeline funnel. */
  showQuoteFunnel: boolean
  /** Per-agent performance table. Never gates the leaderboard beside it. */
  showAgentPerformance: boolean
}

export function useDashboardWidgetFlags(): DashboardWidgetFlags {
  const user = useAuthStore((s) => s.user)
  const flags = user?.feature_flags

  return {
    showLeadsStatusChart: flags?.dashboard_leads_status_chart_enabled !== false,
    showQuoteFunnel: flags?.dashboard_quote_funnel_enabled !== false,
    showAgentPerformance: flags?.dashboard_agent_performance_enabled !== false,
  }
}
